import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveRedEyeRoundedIcon from '@mui/icons-material/RemoveRedEyeRounded';
import Snackbar, { SnackbarCloseReason } from '@mui/material/Snackbar';
import "./CollapsibleTable.css";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { URL } from "../../constants/constant";
import Gradient from "javascript-color-gradient";
import {forwardAnalyticsEvent} from "../../util/sendAnalyticsEvent"


const absoluteMinimumThreshold = -17;
const abslouteMaximumThreshold = 5;

//gradient from red to yellow to blue
const anomalyGradientArray = new Gradient()
.setColorGradient("#ADD8F6", "#ff6633" ).setMidpoint(abslouteMaximumThreshold - absoluteMinimumThreshold + 2)
.getColors();

function scaleValue(value, oldMin = 0, oldMax = 100, newMin = absoluteMinimumThreshold, newMax = abslouteMaximumThreshold) {
  value = Math.max(Math.min(value, oldMax), oldMin);
  const scaledValue = newMin + ((value - oldMin) * (newMax - newMin)) / (oldMax - oldMin);
  return scaledValue;
}

function getAnomalyBoundary(threshold){
  const anomalyBoundary = scaleValue(threshold, 0, 100, absoluteMinimumThreshold, abslouteMaximumThreshold);
  return anomalyBoundary
  
}

function getColorIndex(value, anomalyBoundary) {
  // Define the range of values and the number of colors
  const minVal = Math.round(anomalyBoundary) - absoluteMinimumThreshold;

  const maxVal = abslouteMaximumThreshold - absoluteMinimumThreshold;
  const colorCount = maxVal + 2;

  // Round the value to the nearest integer
  const roundedValue = Math.round(value) - absoluteMinimumThreshold;

  // Check the conditions for value ranges
  if (roundedValue < minVal) {
    return 0;
  } else if (roundedValue > maxVal) {
    return colorCount - 1; 
  } else {
    return roundedValue - minVal;
  }
}

function shouldHighlightRow(row) {
  const { optimalThreshold, anomaly_scores, value, rowText } = row;

  return (
    (optimalThreshold && anomaly_scores && anomaly_scores.reconstruction_loss >= optimalThreshold) ||
    ((optimalThreshold == null || optimalThreshold === Infinity) && !value) ||
    ((optimalThreshold == null || optimalThreshold === Infinity) && value && rowText === 'N/A') ||
    ((optimalThreshold == null || optimalThreshold === Infinity) && value && !rowText) ||
    ((optimalThreshold != null || optimalThreshold === Infinity) && !value) ||
    (optimalThreshold != null && value && rowText === 'N/A') ||
    (optimalThreshold != null && value && !rowText) ||
    ((optimalThreshold == null || optimalThreshold === Infinity) && value && rowText)
  );
}


const Row = ({
  rows,
  rows2,
  setRows2,
  row,
  rowIdx,
  isNormalAbnormal,
  normalAbnormalValue,
  normalAbnormalCommentValue,
  editable,
  pastUser,
  state,
  handlePageChange,
  profile,
  profileName,
  hasMER,
  anomalyBoundary,
  rowOpenedCallBack,
  nonWhite
}) => {

  const [open, setOpen] = useState(false);
  const [isEdit, setEdit] = useState(false);
  const [disable, setDisable] = useState(true);
  const [value, setValue] = useState(normalAbnormalValue || "Normal");
  const [commentValue, setCommentValue] = useState(
    normalAbnormalCommentValue || ""
  );
  const pageNumber = row.pageNumber;
  const [inputErrorSnackbarOpen, setInputErrorSnackbarOpen] = useState(false);
  const [snackbarErrorMessage, setSnackbarErrorMessage] = useState("");

  let dropDownOpenedCount = useRef(0);

  //
  useEffect(() => {
    setEdit(false);
  }, [rows]);

  useEffect(() => {
    if(dropDownOpenedCount.current == 0 && open) {
      rowOpenedCallBack();
      dropDownOpenedCount.current = dropDownOpenedCount.current + 1;
    }
  }, [open]);

  // Function to handle edit
  const handleEdit = () => {
    // If edit mode is true setEdit will
    // set it to false and vice versa
    setEdit(!isEdit);
  };

  const handleInputChange = (e, index, ParentRow) => {
    setDisable(false);
    const { name, value } = e.target;
    const list = [...rows];
    let idx = null;
    // console.log(ParentRow)
    list.map((item, i) => {
      if (item.testName === ParentRow.testName) {
        idx = i;
        // console.log("idx found -> " + idx)
      }
    });
    if (idx === null) {
      alert("no idx");
      return;
    }

    list[idx]["extractedResults"][index][name] = value;
    setRows2(list);
  };


  const handleInputBlur = (e, index, ParentRow) => {
    //setDisable(false);
    const { name, value } = e.target;
    const list = [...rows];
    let idx = null;
    // console.log(ParentRow)
    list.map((item, i) => {
      if (item.testName === ParentRow.testName) {
        idx = i;
        // console.log("idx found -> " + idx)
      }
    });
    if (idx === null) {
      alert("no idx");
      return;
    }


    try {
      let lowerRange = 0;
      let upperRange = Infinity;

      if (list[idx]["extractedResults"][index]["lowerRange"]) {
        lowerRange = parseFloat(list[idx]["extractedResults"][index]["lowerRange"]);
      }
      if (list[idx]["extractedResults"][index]["upperRange"]) {
        upperRange = parseFloat(list[idx]["extractedResults"][index]["upperRange"]);
      }

      if((value < lowerRange || value > upperRange) && value !== "") {
        const inputValue = parseFloat(value);
        if (lowerRange == 0){
          lowerRange = 'None'
        }
        if (upperRange == Infinity){
          upperRange = 'None'
        }
        const snackBarErrorMessage = `Warning: The input value ${inputValue} for "${list[idx]["extractedResults"][index]['subtestName']}" was outside the range (${lowerRange} to ${upperRange}) `
        setInputErrorSnackbarOpen(true)
        setSnackbarErrorMessage(snackBarErrorMessage)

      }

    } catch {
      setSnackbarErrorMessage(`Unknown error occured`)
      setInputErrorSnackbarOpen(true)
    }
    
  };

  const handleRadioBtnChange = (event, ParentRow) => {
    //console.log(event.target.value)
    setValue(event.target.value);

    const list = [...rows];
    let idx = null;

    //find the index of the testName of ParentRow
    list.map((item, i) => {
      if (item.testName === ParentRow.testName) {
        idx = i;
        // console.log("idx found -> " + idx)
      }
    });
    if (idx === null) {
      alert("no idx");
      return;
    }

    list[idx]["normalAbnormalValue"] = event.target.value;

    setRows2(list);
  };

  const handleNormalAbnormalCommentValueChange = (event, ParentRow) => {
    setCommentValue(event.target.value);

    const list = [...rows];
    let idx = null;

    list.map((item, i) => {
      if (item.testName === ParentRow.testName) {
        idx = i;
      }
    });
    if (idx === null) {
      alert("no idx");
      return;
    }

    list[idx]["normalAbnormalCommentValue"] = event.target.value;

    setRows2(list);
  };

  const handleNA = (row) => {
    if (isEdit) {
      const list = [...rows];
      let idx = null;

      list.map((item, i) => {
        if (item.testName === row.testName) {
          idx = i;
        }
      });
      if (idx === null) {
        alert("no idx");
        return;
      }

      let newArr = list[idx]["extractedResults"];
      if(newArr){
          newArr.map((item, i) => {
            if(!item.value || item.isNormalAbnormal){
              Object.keys(item).forEach(function (key, index) {
              if (item[key] === "") {
                newArr[i][key] = "N/A";
      }
    });
  }
  });
  list[idx]["extractedResults"] = newArr;
  setRows2(list);
} else {
  return
}
    }
  };

  const handleNO = (row) => {
    console.log(row);
    //call API for N/O - not opted with row.TestName
  };

  const validatePageChange = () => {
    !open && handlePageChange(pageNumber);
  };

  const subTestPageNo = (page) => { 
    handlePageChange(page);
  }

  const getAnomalyScoreDecisionColor = (row, anomalyBoundary) => {
    const isNA = row.rowText === 'N/A' || !row.rowText;
    const hasValue = !!row.value;
    const hasAnomalyScores = !!row.anomaly_scores;
    const hasText = !!row.rowText;
    const thresholdNullOrInfinity = row.optimalThreshold == null || row.optimalThreshold === Infinity;
    const isValueNotDigit = hasValue && isNaN(Number(row.value));
    if (isValueNotDigit) return "white"; // Return red color if the value is not a digit
  
    // Check if the value is empty
    const isEmptyValue = !hasValue;
  
    if (isNA) return "#E5B723";
    if (isEmptyValue) return "#E5B723"; // Return yellow color if the value is empty
  
    const getGradientColor = () => {
      if (hasAnomalyScores) {
        return anomalyGradientArray[getColorIndex(row.anomaly_scores.reconstruction_loss, anomalyBoundary)];
      }
      return "white"; // Fallback in case anomaly_scores is not defined
    };
  
    const cases = [
      {
        condition: thresholdNullOrInfinity && hasValue && !hasAnomalyScores && hasText,
        color:"#ff6633"
      },
      {
        condition: thresholdNullOrInfinity && hasValue && hasAnomalyScores && hasText,
        color: getGradientColor
      },
      
      {
        condition: thresholdNullOrInfinity && hasValue && !hasAnomalyScores && !hasText,
        color: "white"
      },
      {
        condition: row.optimalThreshold == null && hasValue,
        color: "white"
      },
      {
        condition: row.optimalThreshold != null && hasValue && !hasAnomalyScores,
        color: "white"
      },
      {
        condition: hasAnomalyScores && row.optimalThreshold <= row.anomaly_scores.reconstruction_loss,
        color: getGradientColor
      },
      {
        condition: hasAnomalyScores && row.optimalThreshold > row.anomaly_scores.reconstruction_loss,
        color: "white"
      }
    ];
  
    for (const { condition, color } of cases) {
      if (condition) {
        return typeof color === "function" ? color() : color;
      }
    }
  
    return "white"; // Default fallback
  };


  const getAnomalyScoreDecisionColor2 = (row, anomalyBoundary)=> {
    if(!row.name){
      return "white"
    }
    if(!row.anomaly_scores){
      return "white"
    }
    else{
      //return getColorIndex(row.anomaly_scores.reconstruction_loss,anomalyBoundary)
      return anomalyGradientArray[getColorIndex(row.anomaly_scores.reconstruction_loss,anomalyBoundary)]
    }
  }

  
  return (
    <>
   <TableRow
  className={
    row.extractedResults?.some(item => !item.value) ? (
      nonWhite && profile === 'Corporate' ? 'orange-row' :
      !nonWhite && profile !== 'Corporate' ? 'orange-row' :
      'white-row'
    ) : 'white-row'
  }
  sx={{ "& > *": { borderBottom: "unset" } }}
>
     
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => {
              setOpen(!open);
              validatePageChange();
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell component="th" scope="row" align="center">
          {row.testName}
        </TableCell>
        <TableCell align="center">
          {!isEdit ? (
            <>
              <Button
                variant="contained"
                disabled={
                  !editable ||
                  state == "VERIFIED" ||
                  state == "INCOMPLETE" ||
                  pastUser
                }
                onClick={(e) => handleEdit(e)}
              >
                {" "}
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" onClick={() => setEdit(!isEdit)}>
                {" "}
                <CancelIcon />
              </Button>
            </>
          )}
        </TableCell>

      
       <TableCell align="center">
      
        <Button variant="contained" onClick={(e) => handleNA(row)}>
          N/A
        </Button>
     
    </TableCell>
       
        <TableCell align="center">
          <Button variant="contained" onClick={(e) => handleNO(row)}>
            {" "}
            N/O
          </Button>
        </TableCell>
        <TableCell align="center">{row.status}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6} >
          <Collapse in={open} timeout="auto" unmountOnExit >
            <Box sx={{ margin: 1 }}>
              {isNormalAbnormal ? (
                [
                  isEdit ? (
                    <>
                      <div className="normalAbnormalDiv">
                        <FormControl>
                          <RadioGroup
                            row
                            value={value}
                            onChange={(e) => handleRadioBtnChange(e, row)}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                          >
                            <FormControlLabel
                              name="Normal"
                              value="Normal"
                              control={<Radio />}
                              label="Normal"
                            />
                            <FormControlLabel
                              name="Abnormal"
                              value="Abnormal"
                              control={<Radio />}
                              label="Abnormal"
                            />
                          </RadioGroup>
                        </FormControl>
                        <div style={{ marginTop: 15 }}>
                          <TextField
                            id="outlined-basic"
                            label="Comments"
                            variant="outlined"
                            multiline
                            maxRows={4}
                            value={commentValue}
                            onChange={(e) =>
                              handleNormalAbnormalCommentValueChange(e, row)
                            }
                            style={{ width: "70%" }}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="normalAbnormalDiv">
                        <FormControl>
                          <RadioGroup
                            row
                            value={value}
                            onChange={(e) => handleRadioBtnChange(e, row)}
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                          >
                            <FormControlLabel
                              disabled
                              name="Normal"
                              value="Normal"
                              control={<Radio />}
                              label="Normal"
                            />
                            <FormControlLabel
                              disabled
                              name="Abnormal"
                              value="Abnormal"
                              control={<Radio />}
                              label="Abnormal"
                            />
                          </RadioGroup>
                        </FormControl>
                        <div style={{ marginTop: 15 }}>
                          <TextField
                            id="outlined-basic"
                            label="Comments"
                            variant="outlined"
                            multiline
                            disabled
                            maxRows={4}
                            value={commentValue}
                            onChange={(e) =>
                              handleNormalAbnormalCommentValueChange(e, row)
                            }
                            style={{ width: "70%" }}
                          />
                        </div>
                      </div>
                    </>
                  ),
                ]
              ) : (
                <>
                  <Table size="medium" sx={{ backgroundColor: "#dddce3" }}>
                    <TableHead>
                      <TableRow>
                       <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                        >
                          PageNo
                        </TableCell>
                        <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                        >
                          SubtestName
                        </TableCell>
                        <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                        >
                          Keyword
                        </TableCell>
                        <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                          align="right"
                        >
                          Value
                        </TableCell>
                        <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                          align="right"
                        >
                          Unit
                        </TableCell>
                        
                        <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                          align="right"
                        >
                          Lower Range
                        </TableCell>
                        <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                          align="right"
                        >
                          Upper Range
                        </TableCell>
                        {profile === 'Corporate' && (
                          <TableCell
                          className="customTableCell"
                          sx={{ fontWeight: "bold" }}
                          align="right"
                        >
                          Range
                        </TableCell>
                        )}                     
                        
                      </TableRow>
                    </TableHead>
                  {profile === 'Corporate'?
                  (
                    <TableBody>
                    
                      {isEdit ? (
                        nonWhite ?(
                        <>
                    


                          {row.extractedResults.map((NestedTableRow, i) => (
                            (
                              shouldHighlightRow(NestedTableRow)

                            ) &&
                           
                            <TableRow key={NestedTableRow.subtestName} style={{"backgroundColor" : getAnomalyScoreDecisionColor(NestedTableRow, anomalyBoundary)}}>
                              <TableCell 
                                className="customTableCell"
                                component="th"
                                scope="row">
                                  <RemoveRedEyeRoundedIcon onClick= {() =>subTestPageNo(NestedTableRow.page_num)}/>
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                component="th"
                                scope="row"
                              >
                                {NestedTableRow.subtestName}
                              </TableCell>
                              <TableCell className="customTableCell">
                                {/* try to change dimensions later */}
                                <input
                                  style={{ width: "80%", height: "100%"}}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i]["name"]
                                  }
                                  autoComplete="off"
                                  name="name"
                                  onChange={(e) => handleInputChange(e, i, row)}
                                 
                                />
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                <input
                                  style={{ width: "80%", height: "100%" }}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i][
                                      "value"
                                    ]
                                  }
                                  autoComplete="off"
                                  name="value"
                                  onChange={(e) => handleInputChange(e, i, row)}
                                  onBlur={(e) => handleInputBlur(e, i, row)}
                                />
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                
                                {
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "unit"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="unit"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                }
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "lowerRange"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="lowerRange"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                }
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "upperRange"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="upperRange"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                }
                              </TableCell>

                              {profile === 'Corporate' &&
                                <TableCell
                                  className="customTableCell"
                                  align="right"
                                >
                                  {(profile === "Corporate" && (profileName !== "Retail_Corporate" && profileName !== "Reliance Digital Health Ltd")) ? (
                                  <>{NestedTableRow.range}</>
                                ) : (
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "range"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="range"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                )}
                                  
                                </TableCell>
                              } 
                              
                            </TableRow>
                          ))}
                        </>
                      ):(

                        <>
                          {row.extractedResults.map((NestedTableRow,i) => (
                             (
                              (NestedTableRow.optimalThreshold &&
                              NestedTableRow.anomaly_scores &&
                              NestedTableRow.anomaly_scores.reconstruction_loss < NestedTableRow.optimalThreshold    
                              
                              )
                                &&
                                NestedTableRow.rowText!=''
                                
                            ) &&
                            
                            <TableRow key={NestedTableRow.subtestName} style={{"backgroundColor" : getAnomalyScoreDecisionColor(NestedTableRow, anomalyBoundary)}}>
                            <TableCell 
                              className="customTableCell"
                              component="th"
                              scope="row">
                                <RemoveRedEyeRoundedIcon onClick= {() =>subTestPageNo(NestedTableRow.page_num)}/>
                            </TableCell>
                            <TableCell
                              className="customTableCell"
                              component="th"
                              scope="row"
                            >
                              {NestedTableRow.subtestName}
                            </TableCell>
                            <TableCell className="customTableCell">
                              {/* try to change dimensions later */}
                              <input
                                style={{ width: "80%", height: "100%"}}
                                value={
                                  rows2[rowIdx]["extractedResults"][i]["name"]
                                }
                                autoComplete="off"
                                name="name"
                                onChange={(e) => handleInputChange(e, i, row)}
                               
                              />
                            </TableCell>
                            <TableCell
                              className="customTableCell"
                              align="right"
                            >
                              <input
                                style={{ width: "80%", height: "100%" }}
                                value={
                                  rows2[rowIdx]["extractedResults"][i][
                                    "value"
                                  ]
                                }
                                autoComplete="off"
                                name="value"
                                onChange={(e) => handleInputChange(e, i, row)}
                                onBlur={(e) => handleInputBlur(e, i, row)}
                              />
                            </TableCell>
                            <TableCell
                              className="customTableCell"
                              align="right"
                            >
                              
                              {
                                <input
                                  style={{ width: "80%", height: "100%" }}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i][
                                      "unit"
                                    ]
                                  }
                                  autoComplete="off"
                                  name="unit"
                                  onChange={(e) =>
                                    handleInputChange(e, i, row)
                                  }
                                />
                              }
                            </TableCell>
                            <TableCell
                              className="customTableCell"
                              align="right"
                            >
                              {
                                <input
                                  style={{ width: "80%", height: "100%" }}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i][
                                      "lowerRange"
                                    ]
                                  }
                                  autoComplete="off"
                                  name="lowerRange"
                                  onChange={(e) =>
                                    handleInputChange(e, i, row)
                                  }
                                />
                              }
                            </TableCell>
                            <TableCell
                              className="customTableCell"
                              align="right"
                            >
                              {
                                <input
                                  style={{ width: "80%", height: "100%" }}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i][
                                      "upperRange"
                                    ]
                                  }
                                  autoComplete="off"
                                  name="upperRange"
                                  onChange={(e) =>
                                    handleInputChange(e, i, row)
                                  }
                                />
                              }
                            </TableCell>

                            {profile === 'Corporate' &&
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {(profile === "Corporate" && (profileName !== "Retail_Corporate" && profileName !== "Reliance Digital Health Ltd")) ? (
                                <>{NestedTableRow.range}</>
                              ) : (
                                <input
                                  style={{ width: "80%", height: "100%" }}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i][
                                      "range"
                                    ]
                                  }
                                  autoComplete="off"
                                  name="range"
                                  onChange={(e) =>
                                    handleInputChange(e, i, row)
                                  }
                                />
                              )}
                                
                              </TableCell>
                            } 
                            
                          </TableRow>
                            
                          ))}
                        </>
                      )) : (
                        nonWhite ?(
                        <>
                         {row.extractedResults.map((NestedTableRow, i) => (
                            (
                              shouldHighlightRow(NestedTableRow)
        
                            ) &&
                            
                            <TableRow key={NestedTableRow.subtestName} className={ !NestedTableRow.value ? 'red-row' : null} style={{
                              "backgroundColor" : getAnomalyScoreDecisionColor(NestedTableRow, anomalyBoundary)
                            }}>
                              <TableCell 
                                className="customTableCell"
                                component="th"
                                scope="row">
                                <RemoveRedEyeRoundedIcon onClick= {() =>subTestPageNo(NestedTableRow.page_num)}/>  
                              </TableCell>
                              
                              <TableCell
                                className="customTableCell"
                                component="th"
                                scope="row"
                              >
                                {NestedTableRow.subtestName}
                              </TableCell>
                              <TableCell className="customTableCell">
                                {NestedTableRow.name}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.value}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.unit}
                              </TableCell>
                              
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.lowerRange}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.upperRange}
                              </TableCell>

                              {profile === 'Corporate' ? (
                                <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.range}
                              </TableCell>
                              ): null}
                              
                          </TableRow>
                            
                          ))}
                        </>
                        ):(
                          <>
                          {row.extractedResults.map((NestedTableRow) => (
                             (
                              (NestedTableRow.optimalThreshold &&
                              NestedTableRow.anomaly_scores &&
                              NestedTableRow.anomaly_scores.reconstruction_loss < NestedTableRow.optimalThreshold    
                              
                              )
                                &&
                                NestedTableRow.rowText!=''
                                
                            ) &&
                            
                            <TableRow key={NestedTableRow.subtestName} className={ !NestedTableRow.value ? 'red-row' : null} style={{
                              "backgroundColor" : getAnomalyScoreDecisionColor(NestedTableRow, anomalyBoundary)
                            }}>
                              <TableCell 
                                className="customTableCell"
                                component="th"
                                scope="row">
                                <RemoveRedEyeRoundedIcon onClick= {() =>subTestPageNo(NestedTableRow.page_num)}/>  
                              </TableCell>
                              
                              <TableCell
                                className="customTableCell"
                                component="th"
                                scope="row"
                              >
                                {NestedTableRow.subtestName}
                              </TableCell>
                              <TableCell className="customTableCell">
                                {NestedTableRow.name}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.value}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.unit}
                              </TableCell>
                              
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.lowerRange}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.upperRange}
                              </TableCell>

                              {profile === 'Corporate' ? (
                                <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.range}
                              </TableCell>
                              ): null}
                              
                          </TableRow>
                            
                          ))}
                        </>
                        )
                      )}
                    
                    </TableBody>
                  ):( <TableBody>
                      {isEdit ? (
                        <>
                          {row.extractedResults.map((NestedTableRow, i) => (
                            <TableRow key={NestedTableRow.subtestName} style={{"backgroundColor" : getAnomalyScoreDecisionColor2(NestedTableRow, anomalyBoundary)}}>
                              <TableCell 
                                className="customTableCell"
                                component="th"
                                scope="row">
                                  <RemoveRedEyeRoundedIcon onClick= {() =>subTestPageNo(NestedTableRow.page_num)}/>
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                component="th"
                                scope="row"
                              >
                                {NestedTableRow.subtestName}
                              </TableCell>
                              <TableCell className="customTableCell">
                                {/* try to change dimensions later */}
                                <input
                                  style={{ width: "80%", height: "100%"}}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i]["name"]
                                  }
                                  autoComplete="off"
                                  name="name"
                                  onChange={(e) => handleInputChange(e, i, row)}
                                 
                                />
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                <input
                                  style={{ width: "80%", height: "100%" }}
                                  value={
                                    rows2[rowIdx]["extractedResults"][i][
                                      "value"
                                    ]
                                  }
                                  autoComplete="off"
                                  name="value"
                                  onChange={(e) => handleInputChange(e, i, row)}
                                  onBlur={(e) => handleInputBlur(e, i, row)}
                                />
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                
                                {
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "unit"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="unit"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                }
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "lowerRange"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="lowerRange"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                }
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "upperRange"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="upperRange"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                }
                              </TableCell>

                              {profile === 'Corporate' &&
                                <TableCell
                                  className="customTableCell"
                                  align="right"
                                >
                                  {(profile === "Corporate" && (profileName !== "Retail_Corporate" && profileName !== "Reliance Digital Health Ltd")) ? (
                                  <>{NestedTableRow.range}</>
                                ) : (
                                  <input
                                    style={{ width: "80%", height: "100%" }}
                                    value={
                                      rows2[rowIdx]["extractedResults"][i][
                                        "range"
                                      ]
                                    }
                                    autoComplete="off"
                                    name="range"
                                    onChange={(e) =>
                                      handleInputChange(e, i, row)
                                    }
                                  />
                                )}
                                  
                                </TableCell>
                              } 
                              
                            </TableRow>
                          ))}
                        </>
                      ) : (
                        <>
                          {row.extractedResults.map((NestedTableRow) => (
                            
                            <TableRow key={NestedTableRow.subtestName} className={ !NestedTableRow.value ? 'red-row' : null} style={{
                              "backgroundColor" : getAnomalyScoreDecisionColor2(NestedTableRow, anomalyBoundary)
                            }}>
                              <TableCell 
                                className="customTableCell"
                                component="th"
                                scope="row">
                                <RemoveRedEyeRoundedIcon onClick= {() =>subTestPageNo(NestedTableRow.page_num)}/>  
                              </TableCell>
                              
                              <TableCell
                                className="customTableCell"
                                component="th"
                                scope="row"
                              >
                                {NestedTableRow.subtestName}
                              </TableCell>
                              <TableCell className="customTableCell">
                                {NestedTableRow.name}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.value}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.unit}
                              </TableCell>
                              
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.lowerRange}
                              </TableCell>
                              <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.upperRange}
                              </TableCell>

                              {profile === 'Corporate' ? (
                                <TableCell
                                className="customTableCell"
                                align="right"
                              >
                                {NestedTableRow.range}
                              </TableCell>
                              ): null}
                              
                          </TableRow>
                            
                          ))}
                        </>
                      )}
                    </TableBody>)
                  }
                  </Table>
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Snackbar
        open={inputErrorSnackbarOpen}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={snackbarErrorMessage}
        ContentProps={
          {
            style: {
              backgroundColor: '#f44336',
            }
          }
        }
        onClose={(event, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setInputErrorSnackbarOpen(false);

        }}
      />

    </>
  );
};

const CollapsibleTable = ({
  caseId,
  editable,
  pastUser,
  state,
  handlePageChange,
  isOpen,
  setIsOpen,
  profile,
  rows,
  setRows,
  rows2,
  setRows2,
  assignSnackbarContent,
  profileName,
  hasMER,
  anomalyThresholdValue,
  repeatCaseCount,
  nonWhite,
  setnonWhite
}) => {

  const [isSaving, setIsSaving] = useState(false);
  const handleDialogClose = () => {
    setIsOpen(false);
  };
  const handleConfirm = () => {
    //call API here with rows to persist saved data in db (optimisation -> if no changed dont call api)
    setIsOpen(false);
    setIsSaving(true);
    var data = JSON.stringify({
      caseId: caseId,
      digitization_results: rows,
    });

    forwardAnalyticsEvent("CaseDetailsSaveClicked", { caseId: caseId, currentStatus: state, repeatCaseCount : repeatCaseCount });
      
    var config = {
      method: "post",
      url: URL.demystify_url + "caseDetails/saveCaseDetails/",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(config)
      .then(function (response) {
        setRows(rows2);
        setIsSaving(false);
        assignSnackbarContent("Changes saved");
      })
      .catch(function (error) {
        alert(error);
        setIsSaving(false);
        assignSnackbarContent("An error occurred");
        console.log(error);
      });
  };
  const [anomalyBoundary, setAnomalyBoundary] = useState(-20);
  useEffect(()=>{
    setAnomalyBoundary(getAnomalyBoundary(anomalyThresholdValue))
  }, [anomalyThresholdValue])
  const [collapse, setCollapse] = useState(false);
  const isCorporateProfile = profile === "Corporate";

  return (
    <div>
    <>
      <ConfirmDialog
        isOpen={isOpen}
        handleClose={handleDialogClose}
        handleConfirm={handleConfirm}
      />
  
  {isCorporateProfile ? (
    <div>
     <  TableContainer
        sx={{ marginBottom: 5 }}
        component={Paper}
        className="container"
      >
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#4095f7" }}>
              <TableCell />
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                TestName{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                EditBtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                NAbtn{" "}
              </TableCell>
              
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                NObtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                Status{" "}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {rows && rows.map((row, rowIdx) => {
            
            if (
              row.extractedResults &&
              row.extractedResults.some(shouldHighlightRow)  ||
              row.isNormalAbnormal === true
            ){
           
            return (
              
              <Row
                key={row.testName}
                rows={rows}
                rows2={rows2}
                setRows2={setRows2}
                row={row}
                rowIdx={rowIdx}
                isNormalAbnormal={row.isNormalAbnormal}
                normalAbnormalValue={row.normalAbnormalValue}
                normalAbnormalCommentValue={row.normalAbnormalCommentValue}
                editable={editable}
                pastUser={pastUser}
                state={state}
                handlePageChange={handlePageChange}
                profile={profile}
                hasMER={hasMER}
                profileName={profileName}
                anomalyBoundary={anomalyBoundary}
                nonWhite={true}
                setnonWhite={setnonWhite}
                rowOpenedCallBack={()=>{
                  forwardAnalyticsEvent("DropdownOpened", {caseId: caseId, currentStatus: state, repeatCaseCount : repeatCaseCount });
                }}
              />
            );
          }
        })}     </TableBody>
        </Table>
      </TableContainer>

     
      
      <TableContainer>
    <Table aria-label="collapsible table">
        <TableHead>
            <TableRow style={{ backgroundColor: "#4095f7" }}>
                <TableCell align="center" style={{ fontWeight: "bold", color: "white" }}>
                    <IconButton
                        onClick={() => setCollapse(!collapse)}
                    >
                        {collapse ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                    Anomaly Detection Trusted(99%)
                </TableCell>
            </TableRow>
        </TableHead>
        <Collapse in={collapse} timeout="auto" unmountOnExit>
                <TableContainer>
                <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#4095f7" }}>
              <TableCell />
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                TestName{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                EditBtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                NAbtn{" "}
              </TableCell>
              
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                NObtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                Status{" "}
              </TableCell>
            </TableRow>
          </TableHead>
                        <TableBody>
                        {rows && rows.map((row, rowIdx) => {
                        if (row.extractedResults && row.extractedResults.some(NestedTableRow =>
                       (   NestedTableRow.optimalThreshold &&
                          NestedTableRow.anomaly_scores &&
                          NestedTableRow.anomaly_scores.reconstruction_loss < NestedTableRow.optimalThreshold
                         )
                         &&
                         NestedTableRow.rowText!=''
                          
                        )) {
                         
                          return (
                            <Row
                            key={row.testName}
                              rows={rows}
                              rows2={rows2}
                              setRows2={setRows2}
                              row={row}
                              rowIdx={rowIdx}
                              isNormalAbnormal={row.isNormalAbnormal}
                              normalAbnormalValue={row.normalAbnormalValue}
                              normalAbnormalCommentValue={row.normalAbnormalCommentValue}
                              editable={editable}
                              pastUser={pastUser}
                              state={state}
                              handlePageChange={handlePageChange}
                              profile={profile}
                              hasMER={hasMER}
                              profileName={profileName}
                              anomalyBoundary={anomalyBoundary}
                              nonWhite={false}
                              setnonWhite={setnonWhite}
                              rowOpenedCallBack={()=>{
                                forwardAnalyticsEvent("DropdownOpened", {caseId: caseId, currentStatus: state, repeatCaseCount : repeatCaseCount });
                              }}
                            />
                          );
                        }
                      })}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Collapse>
                </Table>
                </TableContainer>
      
        </div>
      ):( 
      <TableContainer
        sx={{ marginBottom: 5 }}
        component={Paper}
        className="container"
      >
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#4095f7" }}>
              <TableCell />
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                TestName{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                EditBtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                NAbtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                NObtn{" "}
              </TableCell>
              <TableCell
                sx={{ fontWeight: "bold", color: "white" }}
                align="center"
              >
                {" "}
                Status{" "}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          
            {rows &&
            
              rows.map((row, rowIdx) => (
                <Row
                  key={row.testName}
                  rows={rows}
                  rows2={rows2}
                  setRows2={setRows2}
                  row={row}
                  rowIdx={rowIdx}
                  isNormalAbnormal={row.isNormalAbnormal}
                  normalAbnormalValue={row.normalAbnormalValue}
                  normalAbnormalCommentValue={row.normalAbnormalCommentValue}
                  editable={editable}
                  pastUser={pastUser}
                  state={state}
                  handlePageChange={handlePageChange}
                  profile={profile}
                  hasMER={hasMER}
                  profileName={profileName}
                  anomalyBoundary={anomalyBoundary}
                  nonWhite={false}
                  setnonWhite={setnonWhite}
                  rowOpenedCallBack={()=>{
                    forwardAnalyticsEvent("DropdownOpened", {caseId: caseId, currentStatus: state, repeatCaseCount : repeatCaseCount });
                  }}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>)}

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isSaving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
    </div>
  );
};

export default CollapsibleTable;